import { memo, useEffect, useMemo, useState } from 'react';
import type { MemberBadge } from '../../services/pointsService';

interface BadgeNotificationProps {
  badge: MemberBadge;
  show: boolean;
  onClose: () => void;
}

const BadgeNotification: React.FC<BadgeNotificationProps> = ({ badge, show, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const isDev = import.meta.env.DEV;

  if (isDev) {
    console.log('🎊 [BadgeNotification] Renderizando:', { show, badge, hasBadgeData: !!badge?.badge });
  }

  useEffect(() => {
    if (show) {
      if (isDev) {
        console.log('✨ [BadgeNotification] Mostrando modal!');
      }
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [isDev, show]);

  const particles = useMemo(() => {
    if (!show) {
      return [];
    }

    return Array.from({ length: 40 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.3,
      duration: 1.5 + Math.random() * 1
    }));
  }, [show]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  if (!show || !badge.badge) return null;

  // Cores por tier
  const badgeData = badge.badge;
  if (!badgeData) return null;

  const tierColors = {
    bronze: 'from-amber-700 to-amber-900',
    silver: 'from-gray-300 to-gray-500',
    gold: 'from-yellow-400 to-yellow-600',
    diamond: 'from-cyan-300 to-blue-500',
    platinum: 'from-gray-200 to-gray-400'
  };

  const tierGlow = {
    bronze: 'shadow-amber-500/50',
    silver: 'shadow-gray-400/50',
    gold: 'shadow-yellow-500/50',
    diamond: 'shadow-cyan-500/50',
    platinum: 'shadow-gray-300/50'
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />

      {/* Partículas de Celebração */}
      <div className="fixed inset-0 z-50 pointer-events-none overflow-hidden">
        {particles.map(particle => (
          <div
            key={particle.id}
            className="absolute top-0 w-3 h-3 animate-fall"
            style={{
              left: `${particle.left}%`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`
            }}
          >
            <div className={`w-full h-full rounded-full bg-gradient-to-br ${tierColors[badgeData.tier]}`} />
          </div>
        ))}
      </div>

      {/* Modal de Badge */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className={`bg-gradient-to-br from-purple-50 to-blue-50 rounded-3xl shadow-2xl p-8 max-w-md w-full pointer-events-auto transform transition-all duration-500 relative ${
            isVisible ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
          }`}
        >
          {/* Botão Fechar */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white hover:shadow-lg transition-all z-10"
            aria-label="Fechar"
          >
            <i className="ri-close-line text-gray-700 text-xl"></i>
          </button>

          {/* Título */}
          <h2 className="text-3xl font-bold text-center mb-6 text-gray-800 animate-bounce-slow">
            🎉 Novo Badge Desbloqueado!
          </h2>

          {/* Badge Grande com Animação */}
          <div className="flex justify-center mb-6">
            <div className="relative animate-scale-pulse">
              {/* Círculo de fundo pulsante */}
              <div className={`absolute inset-0 bg-gradient-to-br ${tierColors[badgeData.tier]} rounded-full blur-xl opacity-50 animate-ping`}></div>
              
              {/* Badge */}
              <div 
                className={`relative w-32 h-32 rounded-full flex items-center justify-center border-4 border-white bg-gradient-to-br ${tierColors[badgeData.tier]} shadow-2xl ${tierGlow[badgeData.tier]} overflow-hidden`}
              >
                {badgeData.icon && badgeData.icon.startsWith('http') ? (
                  <img 
                    src={badgeData.icon} 
                    alt={badgeData.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-6xl">{badgeData.icon || '🏆'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Nome do Badge */}
          <h3 className="text-2xl font-bold text-center mb-2 text-purple-600">
            {badgeData.name}
          </h3>

          {/* Descrição */}
          {badgeData.description && (
            <p className="text-center text-gray-600 mb-4 text-base">
              {badgeData.description}
            </p>
          )}

          {/* Tier Badge */}
          <div className="flex justify-center mb-6">
            <span className={`px-4 py-2 rounded-full text-sm font-bold text-white bg-gradient-to-r ${tierColors[badgeData.tier]}`}>
              {badgeData.tier.toUpperCase()}
            </span>
          </div>

          {/* Pontos de Recompensa */}
          {badgeData.pointsReward > 0 && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-4 border-2 border-green-200 mb-6">
              <div className="flex items-center justify-center gap-3">
                <i className="ri-star-fill text-yellow-500 text-3xl"></i>
                <span className="text-3xl font-bold text-green-600">+{badgeData.pointsReward}</span>
                <span className="text-gray-600 font-medium">pontos</span>
              </div>
            </div>
          )}

          {/* Metadata (se houver) */}
          {badge.metadata && (
            <div className="bg-white/50 rounded-xl p-3 mb-6">
              <p className="text-sm text-gray-600 text-center">
                {badge.metadata.streakDays && `${badge.metadata.streakDays} dias consecutivos!`}
                {badge.metadata.level && `Nível ${badge.metadata.level} alcançado!`}
              </p>
            </div>
          )}

          {/* Botão de Ação */}
          <button
            onClick={handleClose}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-xl"
          >
            Continuar
          </button>
        </div>
      </div>

      {/* Estilos de Animação */}
      <style>{`
        @keyframes fall {
          0% {
            transform: translateY(-10px) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }

        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes scale-pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        .animate-fall {
          animation: fall linear infinite;
        }

        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }

        .animate-scale-pulse {
          animation: scale-pulse 2s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default memo(
  BadgeNotification,
  (prevProps, nextProps) => {
    const sameBadge = prevProps.badge?.id === nextProps.badge?.id;
    const sameVisibility = prevProps.show === nextProps.show;
    return sameBadge && sameVisibility;
  }
);
